import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable } from 'rxjs';
import { map, filter, switchMap, flatMap } from 'rxjs/operators';

import { DataStore, ShellModel } from '../shell/data-store';
import { StallionListingModel, StallionItemModel } from './listing/stallion-listing.model';
import { StallionDetailsModel } from './details/stallion-details.model';
import { upperFirst } from 'angular-pipes/utils/utils';

class FilterCriteria {
  filterByHeight: boolean;
  height: {
    lower: number;
    upper: number;
  }
  filterByStudfee: boolean;
  currency: string;
  studfee: {
    lower: number;
    upper: number;
  }
  filterByCategories: boolean;
  categories: Array<string> = new Array<string>();
  filterByBreed: boolean;
  breeds: Array<string> = new Array<string>();
  filterByColors: boolean;
  colors: Array<string> = new Array<string>();
  text: string;
}

@Injectable({
  providedIn: 'root'
})
export class StallionService {
  private listingDataStore: DataStore<StallionListingModel>;
  public filter: FilterCriteria;
  public model: StallionListingModel;

  constructor(private http: HttpClient) {
    this.resetFilter();
   }

  public resetFilter() {
    this.filter = new FilterCriteria();
    this.filter.filterByHeight = false;
    this.filter.height = { lower: 80, upper: 190 };
    this.filter.filterByStudfee = false;
    this.filter.currency = '€';
    this.filter.studfee = { lower: 1, upper: 4000 };
    this.filter.filterByCategories = false;
    this.filter.filterByColors = false;
  }

  public getListingDataSource(): Observable<StallionListingModel> {
    
    var httpOptions = new HttpHeaders();
    httpOptions = httpOptions.append("Pragma", "no-cache");
    httpOptions = httpOptions.append("Cache-Control", "no-cache");
        //return this.http.get<StallionListingModel>('./assets/sample-data/stallion/listing.json')
    return this.http.get<StallionListingModel>('https://infoal.blob.core.windows.net/stallions/listing.json', {
      headers: httpOptions
    })
    .pipe( 
      map(
        (data: StallionListingModel) => {
          // Note: HttpClient cannot know how to instantiate a class for the returned data
          // We need to properly cast types from json data
          const listing = new StallionListingModel();

          // The Object.assign() method copies all enumerable own properties from one or more source objects to a target object.
          // Note: If you have non-enummerable properties, you can try a spread operator instead. listing = {...data};
          // (see: https://scotch.io/bar-talk/copying-objects-in-javascript#toc-using-spread-elements-)
          Object.assign(listing, data);

          this.model = listing;
          return listing;
        }
      )
    );
  }

  private isMatchingItem(that: StallionService, data: StallionListingModel & ShellModel): StallionListingModel {
    
    var filter = that.filter;

    if (filter == undefined) {
      return data;
    }

    var dataNew = new StallionListingModel();
    dataNew.isShell = data.isShell;

    var filteredItems:StallionItemModel[] = new Array<StallionItemModel>();
        
    data.items.forEach((item, i) => {            
        var matches : boolean = true;

        if (filter.text !== undefined && filter.text.length > 0) {
          var f = filter.text.toLowerCase();
          if (item.json?.toLowerCase().indexOf(f) < 0) {
            matches = false;
          }
        }

        if (matches && filter.filterByHeight && filter.height != undefined) {
          if (item.height === undefined) {
            matches = false;
          } else if (item.height < filter.height.lower || item.height > filter.height.upper) {
            matches = false;
          }
        }

        if (matches && filter.filterByStudfee && filter.studfee != undefined) {
          if (item.studfee === undefined) {
            matches = false;
          } else {
            var itemCurrency = item.breedingFeeCurrency ?? '€';
            if (itemCurrency === filter.currency) {
              // Währung in Suche ist Währung in Anzeige
              if (item.studfee < filter.studfee.lower || item.studfee > filter.studfee.upper) {
                matches = false;
              }
            } else {
              // Währung in Suche ist ungleich Währung in Anzeige
              var fee = this.convertCurrencyValue(item.studfee, 
                  item.breedingFeeCurrency ?? '€', filter.currency);
              if (fee < filter.studfee.lower || fee > filter.studfee.upper) {
                matches = false;
              }
            } 
          }
        }

        if (matches && filter.filterByBreed && filter.breeds != undefined && filter.breeds !== null) {
          if (item.breed === undefined || item.breed === null || filter.breeds.length === 0
            || item.breed.length === 0) {
            matches = false;
          }
          else {
            matches = item.breed.filter(cat => filter.breeds.includes(cat)).length > 0;
          }
        }

        if (matches && filter.filterByCategories && filter.categories != undefined && filter.categories !== null) {
          if (item.disciplines === undefined || item.disciplines === null || filter.categories.length === 0
            || item.disciplines.length === 0) {
            matches = false;
          }
          else {
            matches = item.disciplines.filter(cat => filter.categories.includes(cat)).length > 0;
          }
        }

        if (matches && filter.filterByColors && filter.colors != undefined && filter.colors !== null) {
          if (item.color === undefined || item.color === null || filter.colors.length === 0
            || item.color.length === 0) {
            matches = false;
          }
          else {
            matches = item.color.filter(cat => filter.colors.includes(cat)).length > 0;
          }
        }

        if (matches) {
          filteredItems.push(item);
        }
      });

    dataNew.items = filteredItems;
    return dataNew;
  }

  convertCurrencyValue(v: number, fromCurrency: string, intoCurrency: string) : number {
    if (fromCurrency === '€' && intoCurrency === '$') {
      return v * 1.2;
    } else if (fromCurrency === '$' && intoCurrency === '€') {
      return v * 0.82;
    }

    return v;
  }

  public getListingStore(dataSource: Observable<StallionListingModel>): DataStore<StallionListingModel> {
    // Use cache if available
    if (!this.listingDataStore) {
      // Initialize the model specifying that it is a shell model
      const shellModel: StallionListingModel = new StallionListingModel();
      this.listingDataStore = new DataStore(shellModel);

      var that = this;
      this.listingDataStore.setMapper((model: StallionListingModel) => {
        return this.isMatchingItem(that, model);
       });
      // Trigger the loading mechanism (with shell) in the dataStore
      this.listingDataStore.load(dataSource);
    }

    return this.listingDataStore;
  }

  public getDetailsDataSource(id: string): Observable<StallionDetailsModel> {
    if (this.model == undefined) {
      const dataSource: Observable<StallionListingModel> = this.getListingDataSource();
      const dataStore: DataStore<StallionListingModel> = this.getListingStore(dataSource);

      var x = dataSource.pipe(
        map(stallionList => stallionList.items.find(i => i.id.toString() === id).detailsUrl),
        switchMap((url: string) => this.http.get<StallionDetailsModel>(url).pipe(
          map((data: StallionDetailsModel) => {
            // Note: HttpClient cannot know how to instantiate a class for the returned data
            // We need to properly cast types from json data
            const details = new StallionDetailsModel();
            // The Object.assign() method copies all enumerable own properties from one or more source objects to a target object.
            // Note: If you have non-enummerable properties, you can try a spread operator instead. details = {...data};
            // (see: https://scotch.io/bar-talk/copying-objects-in-javascript#toc-using-spread-elements-)
            Object.assign(details, data);
            return details;
          })))
        );

      return x;
    }

    var url = this.model.items.find(i => i.id.toString() === id).detailsUrl;

    //return this.http.get<StallionDetailsModel>('./assets/sample-data/stallion/details-' + id + '.json')
    return this.http.get<StallionDetailsModel>(url)
    //return this.http.get<StallionDetailsModel>('./assets/sample-data/stallion/details-' + id + '.json')
    .pipe(
      map(
        (data: StallionDetailsModel) => {
          // Note: HttpClient cannot know how to instantiate a class for the returned data
          // We need to properly cast types from json data
          const details = new StallionDetailsModel();

          // The Object.assign() method copies all enumerable own properties from one or more source objects to a target object.
          // Note: If you have non-enummerable properties, you can try a spread operator instead. details = {...data};
          // (see: https://scotch.io/bar-talk/copying-objects-in-javascript#toc-using-spread-elements-)
          Object.assign(details, data);

          return details;
        }
      )
    );
  }

  public getDetailsStore(dataSource: Observable<StallionDetailsModel>): DataStore<StallionDetailsModel> {
    // Use cache if available
    //if (!this.detailsDataStore) {
      // Initialize the model specifying that it is a shell model
      const shellModel: StallionDetailsModel = new StallionDetailsModel();
      var store = new DataStore(shellModel);
      // Trigger the loading mechanism (with shell) in the dataStore
      store.load(dataSource);
      //this.detailsDataStore = store;

      return store;
    //}
    //return this.detailsDataStore;
  }

  setHeightFilter(lower: any, upper: any) : Observable<StallionListingModel> {
    this.filter.height = { lower: lower, upper: upper };
    return this.listingDataStore.state;
  }

  setStudfeeFilter(lower: any, upper: any) : Observable<StallionListingModel> {
    this.filter.studfee = { lower: lower, upper: upper };
    return this.listingDataStore.state;
  }  
}

